/* eslint-disable react/no-multi-comp */
// lint consider render key value as component definition, hence disabled
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  appTypes,
  timeDuration,
  minDelayDuration,
  productTypes,
  GREENHOUSE,
  BULLHORN,
  mongoDbIdLength,
  apiResponse,
  HUNDRED,
} from 'appConstants';
import { setTemplateTypeIsCustomer, setTemplateTypeIsCustomerFalse } from '../../redux/actions';
import { formatDateTimeForTableView } from 'util/helpers';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { nanoid } from 'nanoid';
import localeConfig from 'config/localeConfig';

export const MESSAGETEMPLATE = 'MessageTemplate';

// TODO: when Security module is in place the product variable below will be obtained from there
const product = 'TextRecruit';
const setFallbackAri = 'SetFallbackAri';
const ari = 'ARI';
const recruitingAri = 'RecruitingAri';
export const defaultRecruitingAri = 'Default Recruiting Ari';
const ompAri = 'OMPAri';
export const defaultOpportunityMarketplaceAri = 'Default Opportunity Marketplace Ari';
const legacyAri = 'Legacy';
const recruitingKnowledgeBase = 'RecruitingAriChatBot';
export const DEFAULT_RECRUITING_ARI_ID = nanoid();
export const DEFAULT_OPPORTUNITY_MARKETPLACE_ARI_ID = nanoid();

export const getSemiTitle = (productType, t) => {
  switch (productType) {
    case appTypes.SALESFORCE:
      return product === 'TextRecruit'
        ? t(
            'search_and_import_candidates_salesforce',
            'Search and import candidates from your SalesForce account',
          )
        : t(
            'search_and_import_employees_salesforce',
            'Search and import employees from your SalesForce account.',
          );
    case appTypes.BULLHORN:
      return product === 'TextRecruit'
        ? t('receive_candidates_status_bullhorn', 'Receive candidates status changes from Bullhorn')
        : t('receive_employees_status_bullhorn', 'Receive employees status changes from Bullhorn');
    case appTypes.EMAIL:
      // TODO: check with Nate: in the below lines, 'Textrecruit will send...' is mentioned (in the exisiting code too). Shouldn't this be product's name - Textrecruit/Textreach etc
      return product === 'TextRecruit'
        ? t(
            'bcc_our_email_gateway_ats_to_candidates',
            'BCC our email gateway on your ATS communications and TextRecruit will send an SMS version of the email to your candidates',
          )
        : t(
            'bcc_our_email_gateway_ats_to_employees',
            'BCC our email gateway on your ATS communications and TextRecruit will send an SMS version of the email to your employees',
          );
    case appTypes.HASAPIENABLED:
      return product === 'TextRecruit'
        ? t(
            'an_external_application_keeping_textrecruit_uptodate',
            'An external application that is keeping Text Engagement up-to-date on new candidates and workflow status changes for your candidate.',
          )
        : t(
            'an_external_application_keeping_textrecruit_uptodate',
            'An external application that is keeping TextRecruit up-to-date on new employees and workflow status changes for your employees',
          );

    default:
      return product === 'TextRecruit'
        ? t(
            'search_and_import_candidates_icims',
            'Search and import candidates from your iCIMS account.',
          )
        : t(
            'search_and_import_employees_icims',
            'Search and import employees from your iCIMS account.',
          );
  }
};

const getKeyForTransalation = (key) => key && key.replace(/ /g, '_');

export const getDuration = (duration, t) =>
  t(getKeyForTransalation(timeDuration[duration]), timeDuration[duration]);

export const getBookingDelay = (delayBookingToNextDay, delay, t) => {
  if (delayBookingToNextDay) {
    return t('next_working_day', 'Next Working Day');
  }
  return t(getKeyForTransalation(minDelayDuration[delay]), minDelayDuration[delay]);
};

export const getKeyByValue = (object, value) =>
  Object.keys(object).find((key) => object[key] === value);

export const setTemplateType = (type, dispatch) => {
  if (type) {
    dispatch(setTemplateTypeIsCustomer());
  } else {
    dispatch(setTemplateTypeIsCustomerFalse());
  }
};
export const setCode = () => `${productTypes.TEXT_RECRUIT.toLowerCase()}`;

/**
 * Extracts the environment from cloud environment string and returns for use as the base path
 *
 * @param {string} cloudEnvironment - The cloud environment string
 *
 * @example
 * // Example input: 'aws-prd'
 * // Example output: 'app'
 *
 * @example
 * // Example input: 'cloud-dev'
 * // Example output: 'dev'
 *
 * @example
 * // Example input: 'cloud-stg'
 * // Example output: 'stg'
 */
export const getBasePath = (cloudEnvironment) => {
  const [, environment] = cloudEnvironment.split('-');
  if (environment === 'prd') {
    return 'app';
  }

  return environment;
};

export const displayPrettyTitle = (title) => {
  const capitalizedTitle = title[0].toUpperCase() + title.slice(1);
  return capitalizedTitle.replace(/([A-Z])/g, ' $1').trim();
};

export const getColumns = (t, classes) => [
  {
    id: 1,
    title: t('category', 'Category'),
    field: 'name',
    sortable: true,
    ariaLabel: t('category', 'Category'),
    headerClassName: classes.categoryHeader,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{displayPrettyTitle(row.data.name)}</>,
  },

  {
    id: 2,
    title: t('description', 'Description'),
    field: 'description',
    sortable: false,
    ariaLabel: t('description', 'Description'),
    headerClassName: classes.descriptionHeader,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.description}</>,
  },

  {
    id: 3,
    title: t('response', 'Response'),
    field: 'response',
    sortable: false,
    ariaLabel: t('response', 'Response'),
    headerClassName: classes.responseHeader,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.response}</>,
  },
];

export const getAriIntentData = (ariIntents, t) =>
  ariIntents.map((response) => ({
    id: response.id,
    data: {
      name: response.name,
      description: response.description,
      response: response.responses.length
        ? response.responses[0].statement
        : t('no_sample_response', 'No sample response available'),
    },
    activateAriaLabel: (row) =>
      t('ari-responseS-list:table.selectRowAriaLabel', 'Select {{ariResponse}}', {
        name: row.data.name,
      }),
  }));

// TODO: will be fixed on ticket 10232

export function useSearch({ searchKey, searchTriggerLength, ariIntents, setFilteredAriIntents }) {
  React.useEffect(() => {
    if (searchKey.length > searchTriggerLength) {
      const searchTermRegExp = new RegExp(searchKey, 'i');
      const filterAriIntents = ariIntents.filter(
        (intent) =>
          displayPrettyTitle(intent.name).match(searchTermRegExp) ||
          intent.description.match(searchTermRegExp),
      );
      setFilteredAriIntents(filterAriIntents);
    }
  }, [searchKey, ariIntents, searchTriggerLength, setFilteredAriIntents]);
}

export const getDefaultSendTimeRestriction = (sendTimeRestriction) => {
  const timeRestrictionKeys = Object.keys(sendTimeRestriction);

  if (timeRestrictionKeys.length) {
    return {
      defaultTimeRestriction: sendTimeRestriction,
      isTimeRestrictionDisabled: false,
    };
  }

  return {
    defaultTimeRestriction: {
      startHourLocal: 0,
      endHourLocal: 0,
      localTimezone: '',
    },
    isTimeRestrictionDisabled: true,
  };
};

export const getSelectedAriType = (selectedAriSequence) => {
  const ariType = [];
  if (selectedAriSequence.states) {
    selectedAriSequence.states.filter((state) => {
      if (state.action === 'ARI' || state.action === 'SetFallbackAri') {
        if (state.jsonData) {
          ariType.push(state.jsonData.ariName);
        }
      }
      return ariType;
    });
  }
  /* passing ariType to fetch intents either from states
  of Sequence object or sending ariType in case of default Ari options */
  /* At times states hold multiple valid actions with no JsonData and 
  hence using filter to fetch the ariName* - TXTE-12165 */
  const result = ariType[0] || selectedAriSequence.ariType;

  return result !== 'Legacy' ? result : recruitingAri;
};

export const hasNlpStep = (sequence) => {
  const isDefaultRecruitingAri = sequence.name === defaultRecruitingAri;
  const isDefaultOmpAri = sequence.name === defaultOpportunityMarketplaceAri;

  if (isDefaultRecruitingAri || isDefaultOmpAri) {
    return true;
  }

  return (
    sequence &&
    sequence.states &&
    sequence.states.some(
      (state) =>
        state.action === setFallbackAri ||
        [recruitingAri, ompAri, legacyAri].includes(state.jsonData?.ariName) ||
        state.action === recruitingKnowledgeBase,
    )
  );
};

export const isKnowledgeBaseDemoSequence = (sequenceName) => {
  /*
   * This regex checks if the sequence name starts with "Digital Assistant Recruiting Demo" followed by an alphanumeric string
   * The alphanumeric string correlates to a MongoDB ObjectId and may be a single ID or optionally followed by subsequent ID's
   * for example: "Digital Assistant Recruiting Demo (64d5e4f8b2c1a3b2c2d2e2f2/64d5e4f8b2c1a3b2c2d2e2f2)"
   */
  const regex =
    /^Digital Assistant Recruiting Demo \(([0-9a-f]{24}|[A-Za-z0-9_-]+)(\/[A-Za-z0-9_-]+)*\)$/;
  return regex.test(sequenceName);
};

export const RenderMoreOptions = ({
  id,
  menuPopover,
  handleUserResponseTemplateClick,
  setOpenDeleteTemplatesModal,
  handleSetTemplateToDelete,
  isEditDisabled,
  isDeleteDisabled,
  t,
}) => {
  const [position, setPosition] = useState(null);
  const open = Boolean(position);

  const handleDripMessagesMenu = (event) => {
    setPosition(event.currentTarget);
  };

  const handleClose = () => {
    setPosition(null);
  };

  return (
    <>
      <IconButton
        aria-label={t('more', 'More')}
        aria-haspopup="true"
        data-testid="message-actions-button"
        onClick={handleDripMessagesMenu}
        disabled={isEditDisabled && isDeleteDisabled}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        data-testid="popover-menu"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        getContentAnchorEl={null}
        anchorEl={position}
        open={open}
        onClose={handleClose}
        classes={{ paper: menuPopover }}
      >
        {!isEditDisabled && (
          <MenuItem
            onClick={
              handleUserResponseTemplateClick &&
              typeof handleUserResponseTemplateClick === 'function'
                ? () => {
                    handleUserResponseTemplateClick(id);
                    handleClose();
                  }
                : null
            }
          >
            {t('edit_template', 'Edit Template')}
          </MenuItem>
        )}

        {!isDeleteDisabled && (
          <MenuItem
            data-testid="delete-template"
            onClick={() => {
              handleSetTemplateToDelete(id);
              setOpenDeleteTemplatesModal(true);
              handleClose();
            }}
          >
            {t('delete', 'Delete')}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

RenderMoreOptions.propTypes = {
  id: PropTypes.string.isRequired,
  menuPopover: PropTypes.string,
  handleUserResponseTemplateClick: PropTypes.func,
  setOpenDeleteTemplatesModal: PropTypes.func.isRequired,
  handleSetTemplateToDelete: PropTypes.func.isRequired,
  isEditDisabled: PropTypes.bool.isRequired,
  isDeleteDisabled: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export const getCustomerRowDataWithMoreOption = ({
  templates,
  classes,
  handleUserResponseTemplateClick,
  setOpenDeleteTemplatesModal,
  handleSetTemplateToDelete,
  t,
}) =>
  templates.map((template) => ({
    id: template.id,
    data: {
      templateName: template.name,
      templateText: template.text,
      createdAt: template.time,
      share: template.share,
      moreOptions: (
        <RenderMoreOptions
          classes={classes}
          handleUserResponseTemplateClick={handleUserResponseTemplateClick}
          setOpenDeleteTemplatesModal={setOpenDeleteTemplatesModal}
          id={template.id}
          handleSetTemplateToDelete={handleSetTemplateToDelete}
          isEditDisabled={!template.hasEditTemplateAccess}
          isDeleteDisabled={!template.hasDeleteTemplateAccess}
          t={t}
        />
      ),
    },
  }));

export const getCustomerRowData = (templates) =>
  templates.map((template) => ({
    id: template.id,
    data: {
      templateName: template.name,
      templateText: template.text,
      createdAt: template.time,
    },
  }));

export const getManageFacebookRows = (templates, t) =>
  templates.map((template) => ({
    data: {
      ...template,
    },
    activateAriaLabel: (row) =>
      t('manage_facebook_page', 'Manage Facebook {{name}}', {
        name: row.data.name,
      }),
  }));

/**
 * This helper function is required as the facebook-info from API response only has SequenceId but not sequence Name
 * We need to update or create new array of facebook objects having sequenceName
 * creating a map of sequenceid as key  and sequence name as value from facebookAriSequences to avoid inner loop
 */
export const generateFacebookRowData = ({ facebookConnections, facebookAriSequences, t }) => {
  const facebookAriMap = {};
  let connections = [];
  facebookAriSequences.forEach((sequence) => (facebookAriMap[sequence.id] = sequence.name));
  facebookConnections.forEach((connection) => {
    const sequenceName = connection.sequenceDefinitionId
      ? facebookAriMap[connection.sequenceDefinitionId]
      : t('none', 'None');
    // In the above line : When there is no sequence mapped to facebook we are assuming its not connected initially and assigning None
    connections = [...connections, { ...connection, sequenceName }];
  });
  return connections;
};

const addDefaultSequences = (sequences) => {
  sequences.push(
    {
      name: defaultRecruitingAri,
      id: DEFAULT_RECRUITING_ARI_ID,
      ariType: recruitingAri,
      supportedLanguages: ['en'],
    },
    {
      name: defaultOpportunityMarketplaceAri,
      id: DEFAULT_OPPORTUNITY_MARKETPLACE_ARI_ID,
      ariType: ompAri,
      supportedLanguages: ['en'],
    },
  );
};

const processState = (sequences, state) => {
  if ((state.action === setFallbackAri || state.action === ari) && state.jsonData) {
    if (state.jsonData.ariName === recruitingAri) {
      sequences.push({
        name: defaultRecruitingAri,
        id: DEFAULT_RECRUITING_ARI_ID,
        ariType: recruitingAri,
        supportedLanguages: ['en'],
      });
    } else if (state.jsonData.ariName === ompAri) {
      sequences.push({
        name: defaultOpportunityMarketplaceAri,
        id: DEFAULT_OPPORTUNITY_MARKETPLACE_ARI_ID,
        ariType: ompAri,
        supportedLanguages: ['en'],
      });
    }
  }
};

const processSequences = (sequences, ariSequences) => {
  ariSequences.forEach((sequence) => {
    (sequence.states || []).forEach((state) => {
      processState(sequences, state);
    });
  });
};

export const getAriSequences = (ariSequences) => {
  const sequences = [];
  if (ariSequences.length === 0) {
    addDefaultSequences(sequences);
  } else {
    processSequences(sequences, ariSequences);
  }
  return sequences.filter(
    (value, index, array) =>
      array.findIndex((element) => element.id === value.id && element.ariType === value.ariType) ===
      index,
  );
};

export function setState(state, action) {
  const { type, payload } = action;
  return {
    ...state,
    [type]: payload,
  };
}

export const isValidSequenceId = (sequenceId) => {
  /*
   * The sequenceId should be a string of length 24 and should contain only hexadecimal characters (0-9, a-f, A-F)
   * This is to ensure that the sequenceId is a valid MongoDB ObjectId rather than a nanoid
   */
  if (typeof sequenceId !== 'string' || sequenceId.length !== mongoDbIdLength) {
    return false;
  }

  // Check if the string contains only hexadecimal characters (0-9, a-f, A-F)
  const hexRegex = /^[0-9a-fA-F]{24}$/;
  return hexRegex.test(sequenceId);
};

const greenhouseStatusColumnData = (_column, row) => <> {row.data.templateName}</>;

const bullhornStatusColumnData = (_column, row) => <> {row.data.templateName}</>;

const templateTextColumnData = (_column, row) => <> {row.data.templateText}</>;

const dateAddedColumnData = (_column, row) => <>{formatDateTimeForTableView(row.data.createdAt)}</>;

const getStatus = (t, name) =>
  t('integeration_status', '{{integrationName}} Status', {
    integrationName: name,
  });

const getTemplateText = (t) => t('template_text', 'Template Text');
const getSelectedAri = (t) => t('selected_ari', 'Selected ARI');

const getDateAdded = (t) => t('date_added', 'Date Added');

export const getGreenhouseColumns = (t, classes) => [
  {
    id: 1,
    title: getStatus(t, GREENHOUSE),
    field: 'name',
    sortable: true,
    ariaLabel: getStatus(t, GREENHOUSE),
    headerClassName: classes.headerTemplateName,
    render: greenhouseStatusColumnData,
  },

  {
    id: 2,
    title: getTemplateText(t),
    field: 'text',
    sortable: false,
    ariaLabel: getTemplateText(t),
    headerClassName: classes.headerTemplateText,
    render: templateTextColumnData,
  },

  {
    id: 3,
    title: getDateAdded(t),
    field: 'time',
    sortable: true,
    ariaLabel: getDateAdded(t),
    headerClassName: classes.headDateAdded,
    render: dateAddedColumnData,
  },
  {
    id: 4,
    title: t('options', 'Options'),
    headerClassName: classes.headerTemplateName,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.moreOptions}</>,
  },
];

export const getBullhornIntegrationColumns = (t, classes) => [
  {
    id: 1,
    title: getStatus(t, BULLHORN),
    field: 'name',
    sortable: true,
    ariaLabel: getStatus(t, BULLHORN),
    headerClassName: classes.headerTemplateName,
    render: bullhornStatusColumnData,
  },

  {
    id: 2,
    title: getTemplateText(t),
    field: 'text',
    sortable: false,
    ariaLabel: getTemplateText(t),
    headerClassName: classes.headerTemplateText,
    render: templateTextColumnData,
  },

  {
    id: 3,
    title: getDateAdded(t),
    field: 'time',
    sortable: true,
    ariaLabel: getDateAdded(t),
    headerClassName: classes.headDateAdded,
    render: dateAddedColumnData,
  },
];

export const getResponseColumns = (t, classes) => [
  {
    id: 1,
    title: t('template_name', 'Template Name'),
    field: 'name',
    sortable: true,
    ariaLabel: t('template_name', 'Template Name'),
    headerClassName: classes.headerTemplateName,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.templateName}</>,
  },

  {
    id: 2,
    title: getTemplateText(t),
    field: 'text',
    sortable: false,
    ariaLabel: getTemplateText(t),
    headerClassName: classes.headerTemplateText,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.templateText}</>,
  },

  {
    id: 3,
    title: getDateAdded(t),
    field: 'time',
    sortable: true,
    ariaLabel: getDateAdded(t),
    headerClassName: classes.headDateAdded,
    render: (_column, row) => formatDateTimeForTableView(row.data.createdAt),
  },

  {
    id: 4,
    title: t('options', 'Options'),
    headerClassName: classes.headerTemplateName,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.moreOptions}</>,
  },
];

export const getResponseColumnsForWhatsapp = (t, classes) => [
  {
    id: 1,
    title: t('template_name', 'Template Name'),
    field: 'name',
    sortable: true,
    ariaLabel: t('template_name', 'Template Name'),
    headerClassName: classes.headerTemplateName,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.templateName}</>,
  },

  {
    id: 2,
    title: getTemplateText(t),
    field: 'text',
    sortable: false,
    ariaLabel: getTemplateText(t),
    headerClassName: classes.headerTemplateText,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.data.templateText}</>,
  },

  {
    id: 3,
    title: getDateAdded(t),
    field: 'time',
    sortable: true,
    ariaLabel: getDateAdded(t),
    headerClassName: classes.headDateAdded,
    render: (_column, row) => formatDateTimeForTableView(row.data.createdAt),
  },
];

export const getManageFacebookColumns = (t, classes, handleEditClick, handleDeleteClick) => [
  {
    id: 1,
    title: t('linked_page', 'Linked Page'),
    field: 'name',
    sortable: false,
    ariaLabel: t('linked_page', 'Linked Page'),
    headerClassName: classes.headerTemplateName,
    render: (_column, row) => row.data.pageName,
  },

  {
    id: 2,
    title: getSelectedAri(t),
    field: 'text',
    sortable: false,
    ariaLabel: getSelectedAri(t),
    headerClassName: classes.headerTemplateText,
    render: (_column, row) => row.data.sequenceName,
  },

  {
    id: 3,
    headerClassName: classes.headDateAdded,
    render: (_column, row) => {
      const editAriaLabel = t('edit', 'Edit', {
        name: row.data.pageId,
      });
      const deleteAriaLabel = t('delete', 'Delete', { name: row.data.pageId });
      return (
        <div className={classes.iconDiv}>
          <IconButton
            onClick={() => {
              handleEditClick(row.data);
            }}
            data-testid="edit_connection"
            className={classes.iconButton}
            aria-label={editAriaLabel}
          >
            <EditIcon className={classes.iconStyle} />
          </IconButton>

          <IconButton
            onClick={() => {
              handleDeleteClick(row.data);
            }}
            className={classes.iconButton}
            aria-label={deleteAriaLabel}
          >
            <DeleteIcon className={classes.iconStyle} />
          </IconButton>
        </div>
      );
    },
  },
];

export const getConnectedFacebookRows = (t, facebookPageData) =>
  facebookPageData.map((page) => ({
    id: page.id,
    name: page.name,
    activateAriaLabel: (row) => {
      t('select_facebook_page', 'Select facebook page {{name}} {{id}}', {
        name: row.name,
        id: row.id,
      });
    },
  }));
export const getConnectedFacebookColumns = (t, classes) => [
  {
    id: 1,
    title: t('name', 'Name'),
    field: 'text',
    sortable: false,
    ariaLabel: t('name', 'Name'),
    headerClassName: classes.headerTemplateText,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.name}</>,
  },
  {
    id: 2,
    title: t('id_uc', 'ID'),
    field: 'name',
    sortable: false,
    ariaLabel: t('id_uc', 'ID'),
    headerClassName: classes.headerTemplateName,
    // eslint-disable-next-line react/display-name
    render: (_column, row) => <>{row.id}</>,
  },
];

export const handleFileDownload = (url, fileName) => {
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
    }, HUNDRED);
  } catch (error) {
    throw error;
  }
};

export const handleActivationResponse = (
  apiResult,
  setIsActivateModalOpen,
  setHasActivated,
  setSnackBarMessage,
  t,
) => {
  if (apiResult.status === apiResponse.SUCCESS) {
    setIsActivateModalOpen(false);
    setHasActivated(true);
    setSnackBarMessage(
      t(
        'digital_assistant_qa_knowledge_base_live',
        'Digital Assistant GenAI Q&A Knowledge Base is now live.',
      ),
    );
    return;
  }

  setIsActivateModalOpen(false);
  if (apiResult.status === apiResponse.ERROR) {
    if (apiResult.message === 'No upgrade occurred') {
      setSnackBarMessage(t('no_upgrade_occurred', 'No upgrade occurred.'));
    } else if (apiResult.message === 'Customer is not eligible for recruiting demo') {
      setSnackBarMessage(
        t(
          'customer_is_not_eligible_for_recruiting_demo',
          'Customer is not eligible for recruiting demo.',
        ),
      );
    }
  }
};

export const getActivationDialogContent = (isSequenceSpecific, sequenceName, t) => {
  if (isSequenceSpecific) {
    return t(
      'activating_knowledge_base_for_sequence_will_replace',
      'Activating Knowledge Base for the sequence "{{sequenceName}}" will replace the Structured Responses sequence "{{sequenceName}}" in your Digital Assistant Q&A. Once activated, this change will apply to all candidate-facing interactions within this sequence. Are you sure you want to proceed?',
      { sequenceName },
    );
  }

  return t(
    'activating_knowledge_base_will_make_it_available',
    'Activating Knowledge Base will make it available for candidate-facing interactions. Are you sure you want to proceed?',
  );
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  // 1 KB = 1024 Bytes
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  // This line calculates the index (i) to determine which unit to use from the sizes array(above). It uses the logarithm of the bytes value divided by the logarithm of k (1024). The Math.floor function rounds down to the nearest whole number.
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))}${sizes[i]}`;
};

const languageEntries = Object.freeze([
  ['bg-BG', 'Bulgarian (Bulgaria)'],
  ['zh-CN', 'Chinese (Simplified)'],
  ['zh-TW', 'Chinese (Traditional)'],
  ['cs-CZ', 'Czech (Czech Republic)'],
  ['da-DK', 'Danish (Denmark)'],
  ['da', 'Danish'],
  ['nl', 'Dutch'],
  ['nl-BE', 'Dutch (Belgium)'],
  ['nl-NL', 'Dutch (Netherlands)'],
  ['en', 'English'],
  ['en-US', 'English (United States)'],
  ['en-GB', 'English (United Kingdom)'],
  ['fi-FI', 'Finnish (Finland)'],
  ['fr-CA', 'French (Canadian)'],
  ['fr-FR', 'French (France)'],
  ['de-DE', 'German'],
  ['el-GR', 'Greek (Greece)'],
  ['id-ID', 'Indonesian (Indonesia)'],
  ['it-IT', 'Italian'],
  ['ja-JP', 'Japanese'],
  ['ko-KR', 'Korean'],
  ['ms-MY', 'Malay (Malaysia)'],
  ['pl-PL', 'Polish'],
  ['pt-BR', 'Portuguese (Brazil)'],
  ['pt-PT', 'Portuguese (Portugal)'],
  ['ro-RO', 'Romanian (Romania)'],
  ['ru-RU', 'Russian'],
  ['sr-RS', 'Serbian (Serbia)'],
  ['es-MX', 'Spanish (Mexico)'],
  ['es-ES', 'Spanish (Spain)'],
  ['sv-SE', 'Swedish'],
  ['th-TH', 'Thai'],
  ['tr-TR', 'Turkish (Turkey)'],
  ['uk-UA', 'Ukrainian (Ukraine)'],
]);

export const LanguageSelectorOptions = languageEntries.map(([code, name]) => ({ code, name }));

export const getLanguageKey = (languageCode) => languageCode;
export const getNegativeResponseKey = (languageCode) => languageCode;

const localeAliasEntries = Object.freeze([
  ['da', 'da-DK'],
  ['de', 'de-DE'],
  ['el', 'el-GR'],
  ['es-419', 'es-MX'],
  ['it', 'it-IT'],
  ['ja', 'ja-JP'],
  ['ko', 'ko-KR'],
  ['ms', 'ms-MY'],
  ['pl', 'pl-PL'],
  ['ru', 'ru-RU'],
  ['sv', 'sv-SE'],
  ['th', 'th-TH'],
]);

const localeAliases = Object.fromEntries(localeAliasEntries);

export const normalizeLanguageCode = (languageCode = '') =>
  localeAliases[languageCode] || languageCode;

/**
 * Ensures the greeting text contains the placeholder {$privacyUrl} instead of an actual URL.
 * This handles cases where old data might have the URL embedded in the greeting text.
 */
export const ensurePlaceholderInGreeting = (greetingText, privacyUrl) => {
  if (!privacyUrl || !greetingText.includes(privacyUrl)) {
    return greetingText;
  }
  // Replace the actual URL with the placeholder
  return greetingText.replace(privacyUrl, '{$privacyUrl}');
};

export const createSortChangeHelper =
  (filter, setFilter, initialSortColumn, rows) =>
  ({ column, direction }) => {
    if (rows.length === 0) {
      return;
    }
    let columnId = null;
    let dir = null;
    let sort = `+${initialSortColumn}`;
    let field = initialSortColumn;

    if (column) {
      columnId = column.id;
      dir = direction;
      // eslint-disable-next-line prefer-destructuring
      field = column.field;
      sort = direction === 'asc' ? `+${field}` : `-${field}`;
    }

    setFilter({
      ...filter,
      sortColumnId: columnId,
      sortDirection: dir,
      sort,
      field,
    });
  };

export const createSortHandler =
  (defaultSortValue, setDefaultSortValue, handleSortChangeHelper) => (column, direction) => {
    const sortColumn = column || defaultSortValue.column;
    const sortDirection = direction || defaultSortValue.direction;

    setDefaultSortValue({
      column,
      direction: direction === 'asc' ? 'desc' : 'asc',
    });
    handleSortChangeHelper({
      column: sortColumn,
      direction: sortDirection,
    });
  };

export async function fetchCdnTranslation(lang, key, fallback = '') {
  const normalizedLanguage = normalizeLanguageCode(lang);

  if (!normalizedLanguage || normalizedLanguage === 'en' || normalizedLanguage === 'en-US') {
    return fallback;
  }

  try {
    const url = `${localeConfig.localesCdn}/${normalizedLanguage}/acs.json`;
    const response = await fetch(url);
    if (!response.ok) {
      return fallback;
    }

    const data = await response.json();
    return data[key] || fallback;
  } catch {
    return fallback;
  }
}
